import React, { useEffect, useState, useRef } from 'react';
import {
    FormGroup,
    FormControlLabel,
    Checkbox,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import axiosInstance from '../../../../lib/axios';
import checkAndLock from '../../../../GlobalFunction/checkAndLock';
import UnLock from '../../../../GlobalFunction/UnLock';
import CrudButtons from '../../../../GlobalFunction/CrudButtons';
import ReportTable from '../../../../atoms/ReportFolder/ReportTable';
import { useReactToPrint } from 'react-to-print';

const columns = [
    { id: 'RowNum', label: 'S.No.', minWidth: 170 },
    { id: 'CommonAreaName', label: 'Area Name', minWidth: 100 },
    { id: 'CommonAreaAbrv', label: 'Abrv', minWidth: 100 },
    { id: 'Remark', label: 'Remark', minWidth: 100 },
];

const CommonArea = () => {
    const [formData, setFormData] = useState({
        CommonAreaName: '',
        CommonAreaAbrv: '',
        Remark: '',
        imageFiles: null,
        CommonAreaImg: '', // Added to store server-returned image URL
    });

    const [error, setError] = useState({
        CommonAreaName: false,
        CommonAreaAbrv: false,
    });

    const navigate = useNavigate();
    const location = useLocation();
    const [isFormDisabled, setIsFormDisabled] = useState(true);
    const [currentAreaId, setCurrentAreaId] = useState(null);
    const [mode, setMode] = useState('view');
    const [status, setStatus] = useState("1");
    const [lastInsertedAreaId, setLastInsertedAreaId] = useState(null);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [userId] = useState(sessionStorage.getItem('UserId'));
    const [dataForPrint, setDataForPrint] = useState({});
    const [rows, setRows] = useState([]);
    const contentRef = useRef(null);
    const handlePrint = useReactToPrint({ contentRef });
    const CommonAreaNameRef = useRef(null);
    const CommonAreaAbrvRef = useRef(null);
    const remarkRef = useRef(null);
    const photoInputRef = useRef(null);

    useEffect(() => {
        if (location.state && location.state.CommonAreaId) {
            setCurrentAreaId(location.state.CommonAreaId);
            fetchCommonArea(location.state.CommonAreaId, "R");
            setMode('view');
        } else {
            setMode('add');
            setIsFormDisabled(false);
        }
    }, [location]);

    useEffect(() => {
        const getRow = async () => {
            const params = {
                start: 0,
                PageSize: 20,
                SearchText: "",
            };
            try {
                const res = await axiosInstance.post('MstCommonArea/GetMstCommonAreaDashboard', { params });
                if (res.data && res.data.Data) {
                    setRows(res.data.Data);
                    setDataForPrint(res.data.Data);
                } else {
                    toast.info('No data found in response');
                }
            } catch (err) {
                toast.error("Error while fetching data");
            }
        };
        getRow();
    }, []);

    const fetchCommonArea = async (id, flag) => {
        try {
            const payload = {
                CommonAreaId: parseInt(id),
                Flag: flag.toLowerCase(),
                CreatedBy: parseInt(userId, 10) || 1,
            };

            const response = await axiosInstance.post(`MstCommonArea/RetriveMstCommonArea`, payload);
            if (!response.data) {
                throw new Error('No data returned in response');
            }

            if (response.data.Status === 0 && response.data.responseStatusCode === 1) {
                let areaData;

                if (Array.isArray(response.data.Data)) {
                    if (response.data.Data.length === 0) {
                        toast.info(`No record found for CommonAreaId: ${id}`);
                        return;
                    }
                    areaData = response.data.Data[0];
                } else if (response.data.Data && typeof response.data.Data === 'object') {
                    areaData = response.data.Data;
                } else if (typeof response.data === 'object' && response.data.CommonAreaName) {
                    areaData = response.data;
                } else {
                    toast.info(`No record found for CommonAreaId: ${id}`);
                    return;
                }

                // Fix image URL if needed
                const fixedImagePath = areaData.CommonAreaImg?.startsWith("http:/")
                    ? areaData.CommonAreaImg.replace("http:/", "http://")
                    : areaData.CommonAreaImg || '';

                setFormData({
                    CommonAreaName: areaData.CommonAreaName || '',
                    CommonAreaAbrv: areaData.CommonAreaAbrv || '',
                    Remark: areaData.Remark || '',
                    imageFiles: null,
                    CommonAreaImg: fixedImagePath, // Store the server-returned image URL
                });
                setStatus(areaData.Status?.toString() || "1");
                setIsFormDisabled(true);
                setCurrentAreaId(areaData.CommonAreaId || id);
            } else {
                toast.error(response.data.Message || 'Failed to fetch area data');
            }
        } catch (error) {
            toast.error('Error fetching area data. Please check the console for details.');
        }
    };

    const handlePrevious = async () => {
        if (currentAreaId && currentAreaId > 1) {
            await fetchCommonArea(currentAreaId, "P");
        }
    };

    const handleNext = async () => {
        if (currentAreaId) {
            await fetchCommonArea(currentAreaId, "N");
        }
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleChangeStatus = (event) => {
        const updatedStatus = event.target.checked ? "1" : "0";
        setStatus(updatedStatus);
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            if (event.target === CommonAreaNameRef.current) {
                CommonAreaAbrvRef.current.focus();
            } else if (event.target === CommonAreaAbrvRef.current) {
                remarkRef.current.focus();
            }
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const maxSize = 2 * 1024 * 1024; // iamge size - 2MB
            const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
            if (!allowedTypes.includes(file.type)) {
                toast.error('You can only upload JPG, JPEG, PNG, or GIF images file type');
                return;
            }
            if (file.size > maxSize) {
                toast.error('Image size must be less than 2MB');
                return;
            }
            setFormData((prevData) => ({
                ...prevData,
                imageFiles: file,
                CommonAreaImg: '', // Clear server image URL when uploading a new file
            }));
        }
    };

    const handleClearImage = () => {
        setFormData((prevData) => ({
            ...prevData,
            imageFiles: null,
            CommonAreaImg: '', // Clear both local file and server image URL
        }));
        if (photoInputRef.current) {
            photoInputRef.current.value = ''; // Reset the file input
        }
    };

    const handleSubmit = async () => {
        let hasError = false;

        // Validate required fields
        if (!formData.CommonAreaName) {
            toast.error('Area Name is required');
            setError((prev) => ({ ...prev, CommonAreaName: true }));
            hasError = true;
        } else {
            setError((prev) => ({ ...prev, CommonAreaName: false }));
        }

        if (hasError) return;

        try {
            const formDataPayload = new FormData();

            // Append data to formDataPayload
            if (mode === 'edit' && currentAreaId) {
                formDataPayload.append("CommonAreaId", currentAreaId);
            }
            formDataPayload.append("CommonAreaName", formData.CommonAreaName);
            formDataPayload.append("CommonAreaAbrv", formData.CommonAreaAbrv);
            formDataPayload.append("Remark", formData.Remark);
            formDataPayload.append("Status", status);

            if (mode === 'edit') {
                formDataPayload.append("UpdatedBy", parseInt(userId, 10));
            } else {
                formDataPayload.append("CreatedBy", parseInt(userId, 10));
            }

            if (formData.imageFiles) {
                formDataPayload.append("imageFiles", formData.imageFiles);
            }

            // Make the API call
            let response;
            if (mode === 'edit') {
                response = await axiosInstance.post("MstCommonArea/UpdateMstCommonArea", formDataPayload, {
                    headers: {
                        "Content-Type": "multipart/form-data"
                    },
                });
            } else {
                response = await axiosInstance.post("MstCommonArea/InsertMstCommonArea", formDataPayload, {
                    headers: {
                        "Content-Type": "multipart/form-data"
                    },
                });
            }

            // Check the response
            if (response.data.Status === 0 && response.data.responseStatusCode === 1) {
                toast.success(response.data.Message);

                // Update formData with the response data
                const newAreaData = response.data.Data; // Assuming this contains the full object
                setFormData({
                    CommonAreaName: newAreaData.CommonAreaName || formData.CommonAreaName,
                    CommonAreaAbrv: newAreaData.CommonAreaAbrv || formData.CommonAreaAbrv,
                    Remark: newAreaData.Remark || formData.Remark,
                    imageFiles: newAreaData.imageFiles || formData.imageFiles,
                    // imageFiles: null, // Reset imageFiles if needed
                    CommonAreaImg: newAreaData.CommonAreaImg || '', // Store the server-returned image URL
                });
                setStatus(newAreaData.Status?.toString() || "1");
                setCurrentAreaId(newAreaData.CommonAreaId || null);
                setMode('view');
                setIsFormDisabled(true);
            } else {
                toast.error(response.data.Message || 'Operation failed');
            }
        } catch (error) {
            toast.error("Error: Could not submit Common Area data", error);
        }
    };

    const handleEdit = async () => {
        let flag = await checkAndLock(currentAreaId, "MstCommonArea", "CommonAreaId");
        switch (flag) {
            case 0:
                setMode("edit");
                setIsFormDisabled(false);
                break;
            case 1:
                toast.info("Record Already Locked", { autoClose: 500 });
                break;
            case 2:
                toast.error("Record Failed To Lock", { autoClose: 500 });
                break;
            default:
                toast.error("Something Went Wrong", { autoClose: 500 });
        }
    };

    const handleAdd = () => {
        setMode('add');
        setIsFormDisabled(false);
        setFormData({
            CommonAreaName: '',
            CommonAreaAbrv: '',
            Remark: '',
            imageFiles: null,
            CommonAreaImg: '',
        });
        setCurrentAreaId(null);
        setStatus("1");

        setTimeout(() => {
            CommonAreaNameRef.current?.focus();
        }, 0);
    };

    const handleCancel = async () => {
        try {
            if (mode === 'edit' && currentAreaId) {
                await UnLock(currentAreaId, "MstCommonArea", "CommonAreaId");
            }
            if (currentAreaId) {
                await fetchCommonArea(currentAreaId, "R");
            } else {
                await fetchCommonArea(1, "L");
            }
            setMode('view');
            setIsFormDisabled(true);
            setStatus("1");
        } catch (error) {
            toast.error('Error occurred while cancelling. Please try again.');
        }
    };

    const handleExit = () => {
        navigate('/commonareatable');
    };

    const handleDeleteClick = () => {
        setOpenConfirmDialog(true);
    };

    const handleCloseConfirmDialog = () => {
        setOpenConfirmDialog(false);
    };

    const handleConfirmDelete = async () => {
        if (!currentAreaId) {
            toast.error("No record selected to delete");
            setOpenConfirmDialog(false);
            return;
        }
        try {
            const flag = await checkAndLock(currentAreaId, "MstCommonArea", "CommonAreaId");
            switch (flag) {
                case 0:
                    await fetchCommonArea(currentAreaId, "D");
                    await fetchCommonArea(currentAreaId, "N");
                    fetchCommonArea(1, "L");
                    setOpenConfirmDialog(false);
                    setStatus("1");
                    setMode('view');
                    setIsFormDisabled(true);
                    toast.success("Record deleted successfully");
                    break;
                case 1:
                    toast.info("Record Already Locked", { autoClose: 500 });
                    break;
                case 2:
                    toast.error("Record Failed To Lock", { autoClose: 500 });
                    break;
                default:
                    toast.error("Something Went Wrong", { autoClose: 500 });
            }
        } catch (error) {
            toast.error('Error deleting record. Please try again.');
        }
    };

    return (
        <>
            <Box className="form-container" sx={{ height: { xs: '700px', sm: '550px' } }}>
                <ToastContainer />
                <Grid container spacing={2} className="form_grid">
                    <Grid
                        item
                        xs={12}
                        className="form_title"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginTop: '20px',
                            flexDirection: { xs: 'column', sm: 'row', md: 'row' },
                        }}
                    >
                        <Grid
                            sx={{
                                display: 'flex',
                                justifyContent: { xs: 'center', sm: 'flex-start' },
                                marginBottom: { xs: '10px', sm: 0 },
                            }}
                        >
                            <Button
                                variant="contained"
                                size="small"
                                className="three-d-button-previous"
                                onClick={handlePrevious}
                                sx={{ backgroundColor: '#635BFF', marginRight: { xs: '5px', sm: '10px' }, display: { xs: 'none', sm: 'block' } }}
                                disabled={mode !== 'view' || !currentAreaId || currentAreaId === 1}
                            >
                                <KeyboardArrowLeftIcon />
                            </Button>

                            <Button
                                variant="contained"
                                size="small"
                                className="three-d-button-next"
                                onClick={handleNext}
                                sx={{ backgroundColor: '#635BFF', display: { xs: 'none', sm: 'block' } }}
                                disabled={mode !== 'view' || !currentAreaId}
                            >
                                <NavigateNextIcon />
                            </Button>
                        </Grid>

                        <Typography
                            variant="h5"
                            sx={{
                                margin: 'auto',
                                textAlign: { xs: 'center', sm: 'center' },
                                fontSize: { xs: '24px', sm: '18px', md: '24px' },
                            }}
                        >
                            Common Area Master
                        </Typography>

                        <Grid sx={{ display: 'flex', justifyContent: 'end' }}>
                            <CrudButtons
                                moduleName="Common Area Master"
                                mode={mode}
                                onAdd={handleAdd}
                                onEdit={handleEdit}
                                onView={handlePrint}
                                onDelete={handleDeleteClick}
                                onExit={handleExit}
                                onPrevious={handlePrevious}
                                onNext={handleNext}
                                readOnlyMode={isFormDisabled}
                            />
                        </Grid>
                    </Grid>

                    <Grid item lg={8} md={8} xs={12}>
                        <Box display="flex" flexDirection="column" gap={2}>
                            <Grid item lg={12} md={12} xs={12}>
                                <Box display="flex" flexDirection="column" gap={2}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6} lg={6}>
                                            <TextField
                                                id="CommonAreaName"
                                                name="CommonAreaName"
                                                label={
                                                    <span>
                                                        Area Name <span style={{ color: 'red' }}>*</span>
                                                    </span>
                                                }
                                                variant="filled"
                                                fullWidth
                                                className="custom-textfield"
                                                value={formData.CommonAreaName}
                                                onChange={handleInputChange}
                                                disabled={isFormDisabled}
                                                error={error.CommonAreaName}
                                                helperText={error.CommonAreaName && ""}
                                                inputRef={CommonAreaNameRef}
                                                onKeyDown={handleKeyPress}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6} lg={6}>
                                            <TextField
                                                id="CommonAreaAbrv"
                                                name="CommonAreaAbrv"
                                                label={<span>Abrv</span>}
                                                variant="filled"
                                                fullWidth
                                                className="custom-textfield"
                                                value={formData.CommonAreaAbrv}
                                                onChange={handleInputChange}
                                                disabled={isFormDisabled}
                                                error={error.CommonAreaAbrv}
                                                inputRef={CommonAreaAbrvRef}
                                                onKeyDown={handleKeyPress}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6} lg={6} className='form_field'>
                                    <TextField
                                        id="Remark"
                                        name="Remark"
                                        label="Remark"
                                        variant="filled"
                                        fullWidth
                                        className="custom-textfield"
                                        value={formData.Remark}
                                        onChange={handleInputChange}
                                        disabled={isFormDisabled}
                                        inputRef={remarkRef}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6} lg={6}>
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    disabled={isFormDisabled}
                                                    checked={status === "1"}
                                                    onChange={handleChangeStatus}
                                                />
                                            }
                                            label="Status"
                                        />
                                    </FormGroup>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>

                    <Grid item lg={4} md={4} xs={12} display="flex" alignItems="center" justifyContent="center">
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            gap={2}
                            border="1px solid #ccc"
                            borderRadius={1}
                            width={130}
                            height={150}
                            overflow="hidden"
                            position="relative"
                        >
                            {formData.imageFiles ? (
                                <img
                                    src={URL.createObjectURL(formData.imageFiles)}
                                    alt="Uploaded Preview"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                    }}
                                />
                            ) : formData.CommonAreaImg ? (
                                <img
                                    src={formData.CommonAreaImg}
                                    alt="Common Area Image"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                    }}
                                    onError={(e) => {
                                        e.target.src = "/fallback-image.png"; // Fallback if server image fails
                                    }}
                                />
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    style={{ textAlign: 'center' }}
                                >
                                    No Image
                                </Typography>
                            )}
                        </Box>
                        <input
                            accept="image/*"
                            style={{ display: 'none' }}
                            id="upload-photo"
                            type="file"
                            onChange={handleFileChange}
                            disabled={isFormDisabled}
                            ref={photoInputRef}
                        />
                        <label htmlFor="upload-photo">
                            <Button
                                component="span"
                                style={{ marginTop: '5px' }}
                                disabled={isFormDisabled}
                            >
                                Upload Image
                            </Button>

                            <Button
                                onClick={handleClearImage}
                                style={{ marginTop: '5px' }}
                                disabled={isFormDisabled || (!formData.imageFiles && !formData.CommonAreaImg)}
                            >
                                Clear Image
                            </Button>
                        </label>

                    </Grid>

                    <Grid item xs={12} className="form_button">
                        {mode === 'view' && (
                            <>
                                <Button
                                    variant="contained"
                                    sx={{ mr: 1, background: 'linear-gradient(290deg, #d4d4d4, #ffffff)' }}
                                    onClick={handleAdd}
                                    disabled
                                >
                                    Submit
                                </Button>
                                <Button
                                    variant="contained"
                                    sx={{ mr: 1, background: 'linear-gradient(290deg, #a7c5e9, #ffffff)' }}
                                    onClick={handleEdit}
                                    disabled
                                >
                                    Cancel
                                </Button>
                            </>
                        )}
                        {(mode === 'edit' || mode === 'add') && (
                            <>
                                <Button variant="contained" sx={{ mr: 1 }} onClick={handleSubmit}>
                                    Submit
                                </Button>
                                <Button variant="contained" sx={{ mr: 1 }} onClick={handleCancel}>
                                    Cancel
                                </Button>
                            </>
                        )}
                    </Grid>
                </Grid>
            </Box>

            <div style={{ display: 'none' }}>
                {Object.keys(dataForPrint).length > 0 && (
                    <ReportTable
                        columns={columns}
                        rows={rows}
                        title="Common Area Master"
                        contentRef={contentRef}
                        customStyles={{ backgroundColor: '#f5f5f5' }}
                    />
                )}
            </div>

            <Dialog
                open={openConfirmDialog}
                onClose={handleCloseConfirmDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"Confirm Deletion"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this record?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        sx={{
                            backgroundColor: '#635bff',
                            color: 'white',
                            '&:hover': { backgroundColor: '#1565c0', color: 'white' },
                        }}
                        onClick={handleConfirmDelete}
                    >
                        Yes
                    </Button>
                    <Button
                        sx={{
                            backgroundColor: '#635bff',
                            color: 'white',
                            '&:hover': { backgroundColor: '#1565c0', color: 'white' },
                        }}
                        onClick={handleCloseConfirmDialog}
                    >
                        No
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CommonArea;